import { helpers } from '@vuelidate/validators';
import { VALIDATION_PATTERNS } from '../constants/validationLimits';

/**
 * Composable for validating template variables according to WhatsApp/Twilio requirements
 * Supports only numeric variables: {{1}}, {{2}}, {{3}}, etc.
 */
export function useVariableValidation() {
  // Limits
  const MAX_VARIABLES = 100;
  const MAX_CONSECUTIVE_SPACES = 4;

  /**
   * Extract all numeric variables from text
   * @param {string} text - The template body text
   * @returns {Array<string>} Array of variable numbers (e.g., ['1', '2', '3'])
   */
  const extractVariables = text => {
    if (!text) return [];

    const matches = text.match(VALIDATION_PATTERNS.NUMERIC_VARIABLE) || [];
    return matches.map(match => match.match(/\d+/)[0]);
  };

  /**
   * Extract all variable placeholders ({{...}}) regardless of content
   * Used for checking variable name format
   */
  const extractAllVariablePlaceholders = text => {
    if (!text) return [];
    return text.match(VALIDATION_PATTERNS.ALL_VARIABLES) || [];
  };

  /**
   * Count non-variable words in text
   * Words are defined as sequences of characters separated by whitespace
   */
  const countNonVariableWords = text => {
    if (!text) return 0;

    // Remove all variables from text
    const textWithoutVars = text.replace(
      VALIDATION_PATTERNS.ALL_VARIABLES,
      ' '
    );

    // Split by whitespace and filter out empty strings
    const words = textWithoutVars
      .split(/\s+/)
      .filter(word => word.trim().length > 0);

    return words.length;
  };

  /**
   * Validator: Variables must be sequential ({{1}}, {{2}}, {{3}})
   * Cannot skip numbers ({{1}}, {{3}} is invalid)
   */
  const validateSequentialNumbering = helpers.withMessage(
    "Variables must be sequential ({'{{'}1{'}}'}, {'{{'}2{'}}'}, {'{{'}3{'}}'}). Cannot skip numbers.",
    value => {
      const variables = extractVariables(value);

      if (variables.length === 0) return true;

      // Convert to numbers and sort
      const numericVars = variables
        .map(v => parseInt(v, 10))
        .filter((v, i, arr) => arr.indexOf(v) === i) // Remove duplicates
        .sort((a, b) => a - b);

      // Check if sequential starting from 1
      for (let i = 0; i < numericVars.length; i += 1) {
        if (numericVars[i] !== i + 1) {
          return false;
        }
      }

      return true;
    }
  );

  /**
   * Validator: Variables cannot be next to each other with only whitespace
   * Example: "{{1}} {{2}}" is invalid, need "{{1}} and {{2}}"
   */
  const validateNoAdjacentVariables = helpers.withMessage(
    "Variables cannot be next to each other with only whitespace. Add words between variables (e.g., \"{'{{'}1{'}}'} and {'{{'}2{'}}'}\").",
    value => {
      if (!value) return true;

      // Check for pattern: }} followed by one or more spaces followed by {{
      const adjacentPattern = /\}\}\s+\{\{/;
      return !adjacentPattern.test(value);
    }
  );

  /**
   * Validator: Message cannot start or end with a variable
   * Need supporting text before/after
   */
  const validateEndpoints = helpers.withMessage(
    'Message cannot start or end with a variable alone. Add text before and after variables.',
    value => {
      if (!value) return true;

      const trimmed = value.trim();

      // Check if starts with variable
      if (/^\{\{[^}]+\}\}/.test(trimmed)) {
        return false;
      }

      // Check if ends with variable
      if (/\{\{[^}]+\}\}$/.test(trimmed)) {
        return false;
      }

      return true;
    }
  );

  /**
   * Validator: For X variables, need at least 2X+1 non-variable words
   * This ensures sufficient non-variable content
   */
  const validateRatio = helpers.withMessage(
    'Too many variables relative to message length. For every X variables, you need at least 2X+1 words.',
    value => {
      if (!value) return true;

      const variables = extractVariables(value);
      const varCount = variables.filter(
        (v, i, arr) => arr.indexOf(v) === i
      ).length; // Unique count

      if (varCount === 0) return true;

      const wordCount = countNonVariableWords(value);
      const requiredWords = varCount * 2 + 1;

      return wordCount >= requiredWords;
    }
  );

  /**
   * Validator: Maximum 100 variables per template
   */
  const validateMaxVariables = helpers.withMessage(
    `Maximum ${MAX_VARIABLES} variables allowed per template.`,
    value => {
      const variables = extractVariables(value);
      const uniqueVars = variables.filter((v, i, arr) => arr.indexOf(v) === i);
      return uniqueVars.length <= MAX_VARIABLES;
    }
  );

  /**
   * Validator: Variable names cannot contain spaces
   * Only numeric variables are allowed: {{1}}, {{2}}, etc.
   */
  const validateOnlyNumericVariables = helpers.withMessage(
    "Only numeric variables are allowed (e.g., {'{{'}1{'}}'}, {'{{'}2{'}}'}). Variable names cannot contain spaces or letters.",
    value => {
      if (!value) return true;

      const allPlaceholders = extractAllVariablePlaceholders(value);

      // Check each placeholder is numeric
      return allPlaceholders.every(placeholder => {
        const content = placeholder.slice(2, -2); // Remove {{ and }}
        return /^\d+$/.test(content);
      });
    }
  );

  /**
   * Get user-friendly count of detected variables
   */
  const getVariableCount = text => {
    const variables = extractVariables(text);
    const uniqueVars = variables.filter((v, i, arr) => arr.indexOf(v) === i);
    return uniqueVars.length;
  };

  /**
   * Get unique variables sorted numerically
   */
  const getUniqueVariables = text => {
    const variables = extractVariables(text);
    const uniqueVars = [...new Set(variables)];
    return uniqueVars.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
  };

  return {
    // Utility functions
    extractVariables,
    getVariableCount,
    getUniqueVariables,
    countNonVariableWords,

    // Validators
    validators: {
      validateSequentialNumbering,
      validateNoAdjacentVariables,
      validateEndpoints,
      validateRatio,
      validateMaxVariables,
      validateOnlyNumericVariables,
    },

    // Constants
    MAX_VARIABLES,
    MAX_CONSECUTIVE_SPACES,
  };
}
